import styled from "@emotion/styled/macro";
import {
  Typography,
  tokens,
  SvgIcon,
  SvgNames,
} from "@sunrun/experience-ui-components";
import { STATUS_CHIP_STATUSES } from "checkout/util/constants";

type StatusChipProps = {
  status: keyof typeof STATUS_CHIP_STATUSES;
  isMobile?: boolean;
};

const StatusChip = ({ status, isMobile }: StatusChipProps) => {
  const statusLabel = {
    signed: "Signed",
    readyToSign: "Ready to Sign",
    locked: "Locked",
    pending: "Form pending",
    pendingCustomerSignature: "Pending customer signature",
    pendingSecondarySigner: "Pending secondary signer",
    pendingSalesRep: "Pending rep counter sign",
    readyToComplete: "Ready to Complete",
    completed: "Completed",
    error: "Error",
  }[status];

  return (
    <Chip $status={status} isMobile={isMobile}>
      <IconWrapper>
        {
          {
            signed: (
              <SvgIcon
                name={SvgNames.CheckEnclosed}
                width="16"
                height="16"
                color={tokens.TINTS_SUCCESS_30}
                data-testid="signed"
              />
            ),
            readyToSign: (
              <SvgIcon
                name={SvgNames.PenToolSvg}
                width="16"
                height="16"
                color={tokens.TINTS_MEDIUM_BLUE_50}
                data-testid="readyToSign"
              />
            ),
            locked: (
              <SvgIcon
                name={SvgNames.LockFilled}
                width="16"
                height="16"
                color={tokens.CHARGERED_20}
                data-testid="locked"
              />
            ),
            pending: (
              <SvgIcon
                name={SvgNames.ErrorFilled}
                width="16"
                height="16"
                data-testid="pending"
                color={tokens.BRAND_HERO_BLUE}
              />
            ),
            pendingCustomerSignature: (
              <SvgIcon
                name={SvgNames.Warning}
                width="16"
                height="16"
                data-testid="pending-customer-signature"
                color={tokens.BRAND_HERO_BLUE}
              />
            ),
            pendingSecondarySigner: (
              <SvgIcon
                name={SvgNames.Warning}
                width="16"
                height="16"
                data-testid="pending-secondary-signer"
                color={tokens.BRAND_HERO_BLUE}
              />
            ),
            pendingSalesRep: (
              <SvgIcon
                name={SvgNames.Warning}
                width="16"
                height="16"
                data-testid="pending-rep"
                color={tokens.BRAND_HERO_BLUE}
              />
            ),
            readyToComplete: (
              <SvgIcon
                name={SvgNames.Warning}
                width="16"
                height="16"
                color={tokens.TINTS_SUCCESS_30}
                data-testid="readyToComplete"
              />
            ),
            completed: (
              <SvgIcon
                name={SvgNames.CheckEnclosed}
                width="16"
                height="16"
                color={tokens.TINTS_SUCCESS_30}
                data-testid="completed"
              />
            ),
            error: (
              <SvgIcon
                name={SvgNames.ErrorFilled}
                width="16"
                height="16"
                data-testid="error"
                color={tokens.CHARGERED_20}
              />
            ),
          }[status]
        }
      </IconWrapper>
      <Typography size="small">{statusLabel}</Typography>
    </Chip>
  );
};

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  width: 16px;
`;

const Chip = styled.div<{
  $status: StatusChipProps["status"];
  isMobile?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ isMobile }) => (isMobile ? "8px 0px" : "8px 32px")};
  gap: 4px;
  border-radius: 6px;
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}

  ${({ $status }) => {
    switch ($status) {
      case "signed":
        return `
            border: 1px solid ${tokens.SUCCESS_70};
            background: ${tokens.SUCCESS_80};

            & > svg {
                & > path {
                    fill: ${tokens.SUCCESS_30} !important;
                }
            }
            
            & > div {
                fill: ${tokens.SUCCESS_30} !important;
                color: ${tokens.SUCCESS_30};
            }
        `;
      case "readyToSign":
        return `
            border: 1px solid ${tokens.TINTS_CHARGE_BLUE_40};
            background: ${tokens.TINTS_CHARGE_BLUE_70};
            fill: ${tokens.TINTS_MEDIUM_BLUE_50};
            & > div {
                color: ${tokens.TINTS_MEDIUM_BLUE_50};
            }
        `;
      case "locked":
      case "error":
        return `
            border: 1px solid ${tokens.TINTS_CHARGE_RED_60};
            background: ${tokens.TINTS_CHARGE_RED_80};
            fill: ${tokens.TINTS_CHARGE_RED_20};
            & > div {
                color: ${tokens.TINTS_CHARGE_RED_20};
            }
        `;
      case "pending":
      case "pendingCustomerSignature":
      case "pendingSecondarySigner":
      case "pendingSalesRep":
        return `
            border: 1px solid ${tokens.TINTS_WARNING_60};
            background: ${tokens.TINTS_WARNING_90};
            fill: ${tokens.BRAND_HERO_BLUE};
            & > div {
                color: ${tokens.BRAND_HERO_BLUE};
            }
        `;
      case "readyToComplete":
        return `
            border: 1px solid ${tokens.TINTS_CHARGE_BLUE_40};
            background: ${tokens.TINTS_CHARGE_BLUE_70};
            fill: ${tokens.TINTS_MEDIUM_BLUE_50};
            & > div {
                color: ${tokens.TINTS_MEDIUM_BLUE_50};
            }
        `;
      case "completed":
        return `
            border: 1px solid ${tokens.SUCCESS_70};
            background: ${tokens.SUCCESS_80};

            & > svg {
                & > path {
                    fill: ${tokens.SUCCESS_30} !important;
                }
            }
            
            & > div {
                fill: ${tokens.SUCCESS_30} !important;
                color: ${tokens.SUCCESS_30};
            }
        `;
    }
  }}
`;

export { StatusChip };
export type { StatusChipProps };
